import { Breadcrumbs, Typography, Link, Stack, TablePagination } from "@mui/material";
import { Link as LinkTo } from "react-router";
import { BackButton } from "../../../../../common/components/back_button";
import { PageTopBar, ThreeSlotLayout } from "../../../../../common/components/page_top_bar";
import { useSatisfacaoClientePesquisaListaViewModel } from "../../viewModels/satisfacao_cliente_pesquisa_lista_view_model";
import { Header } from "./components/header";
import { StyledDataGrid } from "../../../../../common/components/styled_data_grid";
import { useMemo } from "react";
import { GenerateSatisfacaoClientePesquisaListaColumnDefinition } from "./columns";
import { PesquisaSatiscacaoClienteLista } from "../../../domain/models/pesquisa_satisfacao_cliente_lista";
import { ReportViewerModal } from "../../../../../common/components/report_viewer_modal";
import { PesquisaSatisfacaoClienteModal } from "./components/pesquisa_satisfacao_cliente_modal";
import { CreatePesquisaSatisfacaoClienteModal } from "./components/create_pesquisa_satisfacao_cliente_modal";

import RefreshButton from "../../../../../common/components/refresh_button";
import AlertError from "../../../../../common/components/alert_error";
import DynamicIcon from "../../../../../common/components/dynamic_icon";
import PrimaryButton from "../../../../../common/components/primary_button";
import DeleteDialog from "../../../../../common/components/delete_dialog";

/**
 * SatisfacaoClientePesquisaLista
 */

export const SatisfacaoClientePesquisaLista = () => {
  const viewModel = useSatisfacaoClientePesquisaListaViewModel();

  const columnsDefinition = useMemo(() => {
    return GenerateSatisfacaoClientePesquisaListaColumnDefinition({
      onActionEditar: viewModel.handleOpenEditarSatisfacaoClientePesquisa,
      onActionDelete: viewModel.handleOpenDeleteSatisfacaoClientePesquisa,
      onActionExportar: viewModel.handleExportarPesquisaSatisfacaoCliente,
    });
  }, [
    viewModel.handleOpenEditarSatisfacaoClientePesquisa,
    viewModel.handleOpenDeleteSatisfacaoClientePesquisa,
    viewModel.handleExportarPesquisaSatisfacaoCliente,
  ]);

  const pinnedColumns = useMemo(() => {
    return viewModel.pesquisaSatisfacaoClientes.length > 0 ? {
      right: ['idPesquisaSatisfacaoCliente'],
    } : {
    };
  }, [
    viewModel.pesquisaSatisfacaoClientes,
  ]);

  return (
    <>
      {/**
        * PageTopBar
        */}

      <PageTopBar>
        <ThreeSlotLayout
          rightSlot={
            <div className="flex flex-row gap-2">
              <div className="w-[115px]">
                <PrimaryButton
                  startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                  onMouseDown={e => {
                    viewModel.handleCreatePesquisaSatisfacaoClienteOpenState(true);
                    e.preventDefault();
                  }}>
                  Novo Item
                </PrimaryButton>
              </div>

              <BackButton
                onClick={viewModel.handleBack}
              />
            </div>
          }
        />
      </PageTopBar>

      {/**
        * Page
        */}

      <div className="flex w-full gap-2 flex-col py-2 px-4 mb-20">
        <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
          <Link underline="hover" color="inherit">
            Entrega de Obras
          </Link>
          <Link underline="hover" color="inherit" component={'span'}>
            <LinkTo to='/PesquisaCliente_lista'>Satisfação do Cliente</LinkTo>
          </Link>
          <Typography className="text-primary-green">
            Pesquisa de Satisfação por Cliente
          </Typography>
        </Breadcrumbs>

        <div className="flex flex-col gap-0">
          <div className="div flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
            <div className="flex gap-2 items-center w-full">
              <h1 className="text-2xl font-medium text-black dark:text-white">
                Pesquisa de Satisfação por Cliente
              </h1>
              <RefreshButton onClick={viewModel.handleReload} />
            </div>

            <div className="w-full justify-start flex gap-2">
            </div>
          </div>
          {
            viewModel.satisfacaoCliente !== null &&
            <div className="flex flex-row gap-2 text-black dark:text-white">
              <span className="font-bold">Cliente:</span>
              <span>{viewModel.satisfacaoCliente.cliente}</span>
            </div>
          }
        </div>
        {
          viewModel.errorMessage !== undefined
            ? (
              <div className="w-full">
                <AlertError
                  messageErro={viewModel.errorMessage}
                  onTryAgain={viewModel.handleReload} />
              </div>
            ) : (
              <>
                <Header
                  respondidaPor={viewModel.filters.respondidaPor}
                  data={viewModel.filters.data}
                  local={viewModel.filters.local}
                  status={viewModel.filters.status}
                  onRespondidaPorChange={viewModel.handleRespondidaPorChange}
                  onDataChange={viewModel.handleDataChange}
                  onLocalChange={viewModel.handleLocalChange}
                  onStatusChange={viewModel.handleStatusChange}
                  onClearFilters={viewModel.handleClearFilters}
                />

                <Stack spacing={0}>
                  <TablePagination
                    component="div"
                    count={viewModel.pagination.totalItems}
                    page={viewModel.pagination.page - 1}
                    onPageChange={(_, p) => viewModel.handlePageChange(p)}
                    rowsPerPageOptions={[10, 25, 50, 100]}
                    rowsPerPage={viewModel.pagination.rowsPerPage}
                    onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))}
                  />

                  <StyledDataGrid
                    tableName="satisfacao_cliente_pesquisa_lista"
                    disableRowSelectionOnClick={true}
                    disableColumnMenu={false}
                    getRowId={(row: PesquisaSatiscacaoClienteLista) => row.idPesquisaSatisfacaoCliente}
                    columns={columnsDefinition}
                    loading={viewModel.loading}
                    pinnedColumns={pinnedColumns}
                    rows={viewModel.pesquisaSatisfacaoClientes}
                    rowCount={viewModel.pesquisaSatisfacaoClientes.length}
                    pageSizeOptions={[10, 25, 50, 100]}
                    paginationMode="server"
                    paginationModel={{
                      page: viewModel.pagination.page,
                      pageSize: viewModel.pagination.rowsPerPage,
                    }}
                  />
                  <div className="flex flex-row justify-between items-center">
                    <span className="font-bold text-black dark:text-white">
                      Resultado Médio: {
                        new Intl.NumberFormat("pt-BR", {
                          style: 'percent',
                          minimumFractionDigits: 2,
                        }).format(viewModel.resultadoMedio)
                      }
                    </span>

                    <TablePagination
                      component="div"
                      count={viewModel.pagination.totalItems}
                      page={viewModel.pagination.page - 1}
                      onPageChange={(_, p) => viewModel.handlePageChange(p)}
                      rowsPerPageOptions={[10, 25, 50, 100]}
                      rowsPerPage={viewModel.pagination.rowsPerPage}
                      onRowsPerPageChange={(e) => viewModel.handleRowsPerPageChange(parseInt(e.target.value))}
                    />
                  </div>

                </Stack>
              </>
            )
        }
      </div>

      {/**
        * Modals
        */}

      <CreatePesquisaSatisfacaoClienteModal
        open={viewModel.createPesquisaSatisfacaoClienteModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        pesquisaSatisfacaoDropdown={viewModel.pesquisaSatisfacaoDropdown}
        onClose={() => viewModel.handleCreatePesquisaSatisfacaoClienteOpenState(false)}
        onSubmit={viewModel.handleCreatePesquisaSatisfacaoClienteSubmit}
      />

      <PesquisaSatisfacaoClienteModal
        open={viewModel.pesquisaSatifacaoClienteModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        selectedPesquisaSatisfacaoCliente={viewModel.selectedPesquisaSatisfacaoCliente}
        itensPesquisaCliente={viewModel.itensPesquisaCliente}
        onClose={() => viewModel.handlePesquisaSatisfacaoClienteOpenState(null, false)}
        onSubmit={viewModel.handlePesquisaSatisfacaoClienteSubmit}
      />

      <DeleteDialog
        open={viewModel.deletePesquisaSatisfacaoClienteModal}
        saving={viewModel.saving}
        title='Você realmente deseja excluir este item?'
        onClose={() => viewModel.handleOpenDeleteSatisfacaoClientePesquisa(null, false)}
        onDelete={viewModel.handleDeletePesquisaSatisfacaoClienteSubmit}
      />

      <ReportViewerModal
        reportViewerState={viewModel.reportViewerState}
      />

    </>
  );
}